import {Box} from '@mui/material';
import React, {ReactElement, useRef, useState} from 'react';
import {useWorkspaceContext, WorkspaceProvider} from '../../context/workspaceContext';
import WorkspaceHeader from './WorkspaceHeader.component';
import ChatBotEditor from './ChatBotEditor.component';
import SplitPane, {Pane, SashContent} from 'split-pane-react';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import {
    createWorkspaceObject,
    fetchAllResources,
    fetchPagedWorkspaceObjects,
    fetchWorkspaceObject,
    filterOnGroupCanEditResources,
    getResourcesAsPage,
    getSubResourceOnDelete,
    getTreeItemDescendantIds,
    getWorkspaceObjectType,
    getWorkspaceObjectTypeAsNumber
} from '../../utils/resource-utils';
import TabEditor from './tabeditor/TabEditor.component';
import {ResourceType} from '../../@types/resource-type';
import '../../vendor/css/sash.css'

import type {ActionType, ExplorerContainerRef, ExplorerDatasetType, ExtendedTreeViewItem} from './explorer-mrt';
// Import new Explorer components
import {
    canCreateNewItem, createResourceFromTreeItem, ExplorerContainer as NewExplorerContainer, getCreateButtonLabel
} from './explorer-mrt';

import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {API_URL} from '../../config/constants';
import {SSE_EVENT_TYPES} from '../../utils/sse-enums';
import JOB_STATUS_TYPES from '../../utils/job-status-enums';
import PedigreesService from '../../services/pedigrees-service';
import PhasesOfFlightService from '../../services/phases-of-flight.service';
import occurrenceCategoriesService from '../../services/occurrence-categories.service';
import EventSequenceSummary, {OccurrenceCategory, PhaseOfFlight} from '../../@types/event-sequence-summary';
import ResourceBase from '../../@types/resource-base';
import {BasicPage, defaultPageLarge} from '../../@types/spring-data-page';
import OccurrenceService from '../../services/occurrence.service';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import {Moment} from 'moment';
import {useUrlActions} from '../../hooks/useUrlActions';
import eventSequenceService from '../../services/event-sequence.service';
import GroupService from '../../services/group.service';
import safetyService from '../../services/safety.service';
import DeleteResource from './modals/DeleteResource.component';
import LeaveGroupModal from './modals/LeaveGroup.component';
import DuplicateResource from './modals/DuplicateResource.component';
import MoveResource from './modals/MoveResource.component';
import {excludeGroupAndDescendants} from '../../utils/group-utils';
import {exportEventSequence, importEventSequence} from '../../utils/event-sequence-utils';
import {ExportEventSequenceSelectorDialogBox} from '../form-components/ExportEventSequenceSelector.component';
import OccurrenceReportDescriptorService from '../../services/occurrence-report-descriptor.service';
import componentService from '../../services/component.service';
import {ComponentLifecycleTransition, ComponentState, ReleaseLevel} from '../../@types/component';
import PublishComponent from './modals/PublishComponent.component';
import IRPService from '../../services/irp.service';
import safetyRecommendationService, {SyncMode} from '../../services/safety-recommendation.service';
import {useRegisterStore} from '../../stores/safety-recommendation-register.store';
import jobManagementService from '../../services/job-management.service';
import {JobState} from '../../@types/job';
import authService from '../../services/auth.service';
import ServiceUserAccountService from '../../services/service-user-account.service';
import {displayApiKey} from '../../utils/service-user-account-utils';
import {CreateIntegratedRiskPictureDTO} from '../../@types/integrated-risk-picture';
import UserService from '../../services/users.service';


/**
 * There is one Safety Recommendations register, so it is one tab: the tab editor keys tabs by
 * object id, and a second id here would open a second copy fighting the same filter store.
 */
const SAFETY_RECOMMENDATION_REGISTER = {id: 'safetyRecommendations', name: 'Safety Recommendations'};

export default function Workspace(): ReactElement {
    return (
        <WorkspaceProvider>
            <WorkspaceContextAware />
            <ToastContainer
                position="bottom-right"
            />
        </WorkspaceProvider>
    )
}

function WorkspaceContextAware(): ReactElement {
    // Updated panel sizes: removed first panel since new Explorer combines menu and tree
    const [panelSizePercent, setPanelSizePercent] = useState<(number | string)[]>(['24%', '50%', 0]);
    const dimension = { 'height': 'calc(100vh)', 'gridColumn': '1 / -1'};
    const aria = { 'ariaLabel': 'Workspace' };
    const { workspaceObjects, setWorkspaceObjects,
        currentWorkspaceObject, setCurrentWorkspaceObject,
        setOccurrenceCategories, setPhasesOfFlight, setPedigrees, setOccurrenceTypes, getAdditionalResource,
        additionalResources, addOrUpdateAdditionalResource, setDeletedWorkspaceObject, publishResourceUpdate,
        consumePendingGroupDelete } = useWorkspaceContext();
    const tabIndexRef = useRef<number>(0);
    const sseConnectionRef = useRef<EventSource | null>(null);
    const explorerRef = useRef<ExplorerContainerRef | null>(null);
    const importDescriptorInputRef = useRef<HTMLInputElement | null>(null);
    const importMappingInputRef = useRef<HTMLInputElement | null>(null);
    const [importMappingTarget, setImportMappingTarget] = useState<ExtendedTreeViewItem | null>(null);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const { getUrlAction, clearUrlAction } = useUrlActions();
    const [pendingUrlAction, setPendingUrlAction] = useState<any>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    // Authentication is now handled by ProtectedRoute wrapper in App.tsx
    // No need to check auth here - if we're rendering, we're authenticated
    
    // Modal states
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [hardDeleteModalOpen, setHardDeleteModalOpen] = useState(false);
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [leaveGroupModalOpen, setLeaveGroupModalOpen] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState<ExtendedTreeViewItem | null>(null);
    const [resourceToHardDelete, setResourceToHardDelete] = useState<ExtendedTreeViewItem | null>(null);
    const [resourceToDuplicate, setResourceToDuplicate] = useState<ExtendedTreeViewItem | null>(null);
    const [groupToLeave, setGroupToLeave] = useState<ExtendedTreeViewItem | null>(null);
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [resourceToMove, setResourceToMove] = useState<ExtendedTreeViewItem | null>(null);
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [resourceToPublish, setResourceToPublish] = useState<ExtendedTreeViewItem | null>(null);
    const [publishing, setPublishing] = useState(false);
    const [showExportEventSequence, setShowExportEventSequence] = useState(false);
    const [exportSafetyModel, setExportSafetyModel] = useState<ExtendedTreeViewItem | null>(null);
    const [exportEventSequenceList, setExportEventSequenceList] = useState<EventSequenceSummary[]>([]);
    
    // Deletion progress state
    const [isDeleting, setIsDeleting] = useState(false);
    const [deletionProgressMessage, setDeletionProgressMessage] = useState<string | undefined>();

    const onTabClick = (tabIndex: number): void => {
        // Keep track of the tab index so that we can prevent setting the workspace objects if the tab index has changed
        // while fetching the workspace objects.
        tabIndexRef.current = tabIndex;
        let objType = getWorkspaceObjectType(tabIndex);
        setWorkspaceObjects({objectType: objType});
    }

    const processPendingUrlAction = (): void => {
        if (!pendingUrlAction) return;
        
        const resource = new ResourceBase(
            pendingUrlAction.type,
            parseInt(pendingUrlAction.id),
            pendingUrlAction.parentId ? parseInt(pendingUrlAction.parentId) : undefined
        );
        
        // For nested resources, add container information
        if (pendingUrlAction.parentId) {
            (resource as any).containerId = parseInt(pendingUrlAction.parentId);
        }
                
        // Find and open the resource
        if (resource.id) {
            const groups = additionalResources.find(r => r.objectType === ResourceType.GROUPS)?.resources || [];
            fetchWorkspaceObject(resource, groups)?.then((object) => {
                setCurrentWorkspaceObject({objectType: resource.type, object: object});
                // Clear the URL params and pending action after successful processing
                clearUrlAction();
                setPendingUrlAction(null);
            }).catch((error) => {
                toast.error(`Failed to open resource: ${error.message}`);
                // Clear even on error to prevent repeated attempts
                clearUrlAction();
                setPendingUrlAction(null);
            });
        }
    }

    // Handler for dataset changes from new Explorer
    const handleDatasetChange = (dataset: ExplorerDatasetType): void => {
        // dataset is now directly a ResourceType value
        const tabIndex = getWorkspaceObjectTypeAsNumber(dataset);
        onTabClick(tabIndex);
        // The register reads every source at once, so choosing the dataset is enough to open it.
        if (dataset === ResourceType.SAFETY_RECOMMENDATIONS) {
            setCurrentWorkspaceObject({
                objectType: ResourceType.SAFETY_RECOMMENDATIONS,
                object: SAFETY_RECOMMENDATION_REGISTER,
            });
        }
    };

    /**
     * Follows a recommendation sync or rebuild to its end, telling the user how it went and
     * refreshing the source row. A long job is left running: the row's state is read on any later
     * refresh, so nothing is lost by giving up on watching it.
     */
    const watchRecommendationJob = (executionId: number, what: string): void => {
        const POLL_MS = 5000;
        const GIVE_UP_AFTER = 120; // ten minutes
        let polls = 0;
        const refresh = (): void => {
            if (explorerRef.current) {
                explorerRef.current.refresh();
            }
        };
        const poll = (): void => {
            polls += 1;
            jobManagementService.getJobDetails(executionId).then((job) => {
                const state = job.status?.state;
                if (state === JobState.COMPLETED) {
                    toast.success(`${what} finished: ${job.result?.summary || 'done'}`);
                    refresh();
                } else if (state === JobState.FAILED || state === JobState.CANCELLED || state === JobState.TIMED_OUT) {
                    toast.error(`${what} ${state}: ${job.status?.errorMessage || job.status?.message || 'no detail'}`);
                    refresh();
                } else if (polls < GIVE_UP_AFTER) {
                    setTimeout(poll, POLL_MS);
                } else {
                    toast.info(`${what} is still running; the source row will show when it is done.`);
                    refresh();
                }
            }).catch(() => {
                // The job is running on the server whatever the browser can see, so stop quietly.
                refresh();
            });
        };
        setTimeout(poll, POLL_MS);
    };

    /** Starts a sync or rebuild of one recommendation source and follows it. */
    const startRecommendationJob = (
        item: ExtendedTreeViewItem,
        mode: SyncMode | 'rebuild' | 'reindex' | 'taxonomyTag' | 'taxonomyRetag',
    ): void => {
        const source = item.id;
        const JOB_NAMES: Record<string, string> = {
            rebuild: 'rebuild',
            reindex: 'index',
            taxonomyTag: 'taxonomy tagging',
            taxonomyRetag: 'taxonomy re-tagging',
        };
        const what = JOB_NAMES[mode] ? `${source} ${JOB_NAMES[mode]}` : `${source} sync (${mode})`;
        let started;
        if (mode === 'rebuild') {
            started = safetyRecommendationService.startRebuild(source);
        } else if (mode === 'reindex') {
            started = safetyRecommendationService.startIndex(source);
        } else if (mode === 'taxonomyTag' || mode === 'taxonomyRetag') {
            started = safetyRecommendationService.startTaxonomyTagging(source, mode === 'taxonomyRetag');
        } else {
            started = safetyRecommendationService.startSync(source, mode);
        }
        started.then((job) => {
            toast.success(`${what} started`);
            if (explorerRef.current) {
                explorerRef.current.refresh();
            }
            watchRecommendationJob(job.executionId, what);
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    };

    // Handler for resource selection from new Explorer
    const handleResourceSelect = (item: ExtendedTreeViewItem, openTreeEditor = true): void => {
        // For groups, check if user has access before attempting to fetch
        if (item.type === ResourceType.GROUPS) {
            // Check if user has a role for this group (empty string means no access)
            const hasAccess = item.groupRole && item.groupRole !== '';
            
            if (!hasAccess) {
                // User doesn't have access to this group - just show a message
                toast.info(`You don't have access to view the "${item.name}" group details`);
                return;
            }
        }
        
        // A source row narrows the one register to that source rather than opening another copy of
        // it. The filters live in the register's own store, so this reaches the tab wherever it is.
        if (item.type === ResourceType.SAFETY_RECOMMENDATIONS) {
            useRegisterStore.getState().setFilters({sources: [item.id]});
            setCurrentWorkspaceObject({
                objectType: ResourceType.SAFETY_RECOMMENDATIONS,
                object: SAFETY_RECOMMENDATION_REGISTER,
            });
            return;
        }

        let resource = createResourceFromTreeItem(item);
        
        // Special handling for Event Sequences - open the tree editor instead of summary when requested
        if (resource.type === ResourceType.EVENT_SEQUENCES && openTreeEditor) {
            const treeResource = new ResourceBase(ResourceType.EVENT_SEQUENCE_TREES, resource.id, resource.parentId);
            // Preserve containerId if it exists
            if ((resource as any).containerId) {
                (treeResource as any).containerId = (resource as any).containerId;
            }
            resource = treeResource;
        }

        // A Component opens its fault tree canvas the same way a Safety View opens its diagram.
        // Its details are reached from the action menu, as they are for every other resource.
        if (resource.type === ResourceType.COMPONENT && openTreeEditor) {
            resource = new ResourceBase(ResourceType.COMPONENT_TREE, resource.id, resource.parentId);
        }

        addOrSelectResource(resource);
    };

    // Handle IRP duplication
    const handleIRPDuplication = (resourceToDuplicate: ExtendedTreeViewItem, destinationGroupId: number): void => {
        if (resourceToDuplicate.type !== ResourceType.INTEGRATED_RISK_PICTURES) {
            return;
        }

        const reportSearchCriteria = {
            includeAllReports: true,
            descriptorTermIds: []
        }
        const dto: CreateIntegratedRiskPictureDTO = {
            name: `${resourceToDuplicate.object.name} - Copy - ${new Date().toISOString()}`,
            description: resourceToDuplicate.object.description,
            eventSequenceIds: resourceToDuplicate.object.simpleEventSequences.map((es: any) => es.eventSequenceId),
            reportSearchCriteria: resourceToDuplicate.object.reportSearchCriteria ?? reportSearchCriteria
        };

        IRPService.createIntegratedRiskPicture(destinationGroupId, dto).then((newIRP) => {
            setWorkspaceObjects({ objectType: ResourceType.INTEGRATED_RISK_PICTURES });
            // Publish update event
            publishResourceUpdate({
                type: ResourceType.INTEGRATED_RISK_PICTURES,
                id: newIRP.id,
                updatedData: newIRP,
                source: 'save'
            });
                
            setCurrentWorkspaceObject({    
                objectType: ResourceType.INTEGRATED_RISK_PICTURES,
                object: newIRP
            })

            toast.success('IRP duplicated successfully');
        }).catch((error) => {
            toast.error(`Error duplicating IRP - ${error.message}`);
        });
    }

    // Handler for resource double-click
    const handleResourceDoubleClick = (item: ExtendedTreeViewItem): void => {
        // For Event Sequences and Components, double-click opens the tree editor
        const openTreeEditor = item.type === ResourceType.EVENT_SEQUENCES
            || item.type === ResourceType.COMPONENT;
        handleResourceSelect(item, openTreeEditor);
    };

    // Handler for action menu items
    const handleAction = (action: ActionType, item: ExtendedTreeViewItem): void => {
        switch (action) {
            case 'edit':
                // 'Edit' opens the canvas for the two resources that have one.
                if (item.type === ResourceType.EVENT_SEQUENCES || item.type === ResourceType.COMPONENT) {
                    handleResourceSelect(item, true);
                } else {
                    handleResourceSelect(item, false);
                }
                break;
                
            case 'delete':
                setResourceToDelete(item);
                setDeleteModalOpen(true);
                break;
                
            case 'duplicate':
                // For Event Sequences, ensure Safety Models are loaded
                if (item.type === ResourceType.EVENT_SEQUENCES) {
                    const safetyModels = getAdditionalResource(ResourceType.SAFETY_MODELS);
                    if (!safetyModels || !safetyModels.resources) {
                        // Need to load Safety Models first
                        const fetchPromise = fetchAllResources(ResourceType.SAFETY_MODELS);
                        if (fetchPromise) {
                            fetchPromise.then((allSafetyModels) => {
                                addOrUpdateAdditionalResource({ objectType: ResourceType.SAFETY_MODELS, resources: allSafetyModels });
                                setResourceToDuplicate(item);
                                setDuplicateModalOpen(true);
                            }).catch((error) => {
                                toast.error('Error loading safety models: ' + error.message);
                            });
                        }
                    } else {
                        setResourceToDuplicate(item);
                        setDuplicateModalOpen(true);
                    }
                } else if (item.type === ResourceType.INTEGRATED_RISK_PICTURES) {
                    handleIRPDuplication(item, item.object.groupId);
                } else {
                    setResourceToDuplicate(item);
                    setDuplicateModalOpen(true);
                }
                break;
                
            case 'move':
                if (item.type === ResourceType.EVENT_SEQUENCES) {
                    const safetyModels = getAdditionalResource(ResourceType.SAFETY_MODELS);
                    if (!safetyModels || !safetyModels.resources) {
                        const fetchPromise = fetchAllResources(ResourceType.SAFETY_MODELS);
                        if (fetchPromise) {
                            fetchPromise.then((allSafetyModels) => {
                                addOrUpdateAdditionalResource({ objectType: ResourceType.SAFETY_MODELS, resources: allSafetyModels });
                                setResourceToMove(item);
                                setMoveModalOpen(true);
                            }).catch((error) => {
                                toast.error('Error loading safety models: ' + error.message);
                            });
                        }
                    } else {
                        setResourceToMove(item);
                        setMoveModalOpen(true);
                    }
                } else {
                    setResourceToMove(item);
                    setMoveModalOpen(true);
                }
                break;

            case 'newEventSequence':
                if (item.type === ResourceType.SAFETY_MODELS && item.object) {
                    // Create a new EventSequenceSummary object with the safety model ID
                    const newEventSequence: EventSequenceSummary = {} as EventSequenceSummary;
                    newEventSequence.containerId = item.object.id;
                    // Set the workspace object directly instead of going through ResourceBase
                    setCurrentWorkspaceObject({ 
                        objectType: ResourceType.EVENT_SEQUENCES, 
                        object: newEventSequence 
                    });
                }
                break;
                
            case 'importEventSequence':
                if (item.type === ResourceType.SAFETY_MODELS && item.object) {
                    importEventSequence(
                        item.object.id,
                        item.name,
                        (_eventSequence) => {
                            // Refresh the safety models list to show the new event sequence
                            setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
                        }
                    );
                }
                break;
                
            case 'exportEventSequence':
                if (item.type === ResourceType.SAFETY_MODELS && item.object) {
                    // Load event sequences and show selector modal
                    const safetyModelObj = item.object;
                    if (safetyModelObj.numberEventSequences > 0) {
                        // Load event sequences if not already loaded
                        if (!safetyModelObj.children || safetyModelObj.children.length === 0) {
                            safetyService.getEventSequences(safetyModelObj.id).then((eventSequences) => {
                                setExportEventSequenceList(eventSequences);
                                setExportSafetyModel(item);
                                setShowExportEventSequence(true);
                            }).catch((error) => {
                                toast.error('Error getting event sequences - ' + error.message);
                            });
                        } else {
                            setExportEventSequenceList(safetyModelObj.children);
                            setExportSafetyModel(item);
                            setShowExportEventSequence(true);
                        }
                    } else {
                        toast.info('No event sequences to export');
                    }
                } else if (item.type === ResourceType.EVENT_SEQUENCES && item.object) {
                    // Direct export for event sequences - use the full object which should be an EventSequenceSummary
                    exportEventSequence(item.object);
                }
                break;
                
            case 'view':
                // For IRPs, open the workbook view; for other resources, open normally
                if (item.type === ResourceType.INTEGRATED_RISK_PICTURES) {
                    const irp = item.object || item;
                    const workbookResource = {
                        id: irp.id,
                        name: irp.name || 'IRP Workbook',
                        integratedRiskPicture: irp
                    };
                    
                    setCurrentWorkspaceObject({
                        objectType: ResourceType.IRP_WORKBOOK,
                        object: workbookResource
                    });
                } else {
                    handleResourceSelect(item, false);
                }
                break;
                
            case 'newSubGroup':
                if (item.type === ResourceType.GROUPS) {
                    const newGroup = new ResourceBase(ResourceType.GROUPS);
                    (newGroup as any).parent = parseInt(item.id);
                    addOrSelectResource(newGroup);
                }
                break;
                
            case 'leaveGroup':
                if (item.type === ResourceType.GROUPS) {
                    setGroupToLeave(item);
                    setLeaveGroupModalOpen(true);
                }
                break;
                
            case 'properties':
                handleResourceSelect(item, false);
                break;

            case 'mapping':
                // A component's mapping view opens as its own tab, keyed by the component.
                if (item.type === ResourceType.COMPONENT && item.object?.id) {
                    addOrSelectResource(new ResourceBase(ResourceType.COMPONENT_MAPPING, item.object.id));
                }
                break;

            case 'newComponent':
                // From a working area's own menu, so that working area is preselected in the form.
                // Set directly, as newEventSequence does, because the editor seeds its select from
                // the object it is handed.
                if (item.object?.id) {
                    setCurrentWorkspaceObject({ objectType: ResourceType.COMPONENT, object: { groupId: item.object.id } });
                } else {
                    addOrSelectResource(new ResourceBase(ResourceType.COMPONENT));
                }
                break;

            case 'sensitivityAnalysis':
                if (item.type === ResourceType.EVENT_SEQUENCES && item.object) {
                    setCurrentWorkspaceObject({ 
                        objectType: ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS, 
                        object: item.object 
                    });
                }
                break;
                
            case 'uncertaintyAnalysis':
                if (item.type === ResourceType.EVENT_SEQUENCES && item.object) {
                    setCurrentWorkspaceObject({ 
                        objectType: ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS, 
                        object: item.object 
                    });
                }
                break;
                
            case 'versionHistory':
                if (item.type === ResourceType.EVENT_SEQUENCES && item.object) {
                    setCurrentWorkspaceObject({ 
                        objectType: ResourceType.EVENT_SEQUENCE_HISTORY, 
                        object: item.object 
                    });
                } else if (item.type === ResourceType.COMPONENT && item.object?.id) {
                    // Like mapping, a component's history opens as its own tab, keyed by the component.
                    addOrSelectResource(new ResourceBase(ResourceType.COMPONENT_HISTORY, item.object.id));
                }
                break;
                
            case 'deactivate':
                // TODO: Implement deactivate functionality
                toast.info('Deactivate not yet implemented');
                break;
                
            case 'reissueApiKey':
                if (item.type === ResourceType.SERVICE_USER_ACCOUNTS && item.object) {
                    ServiceUserAccountService.reissueServiceUserKey(item.object)
                        .then((updatedServiceUser) => {
                            displayApiKey(updatedServiceUser);
                            if (explorerRef.current) {
                                explorerRef.current.refresh();
                            }
                        })
                        .catch((error) => {
                            toast.error(`Failed to reissue API key: ${error.message}`);
                        });
                }
                break;
                
            case 'publish':
                if (item.type === ResourceType.COMPONENT && item.object) {
                    if (item.object.state !== ComponentState.DRAFT) {
                        toast.error('Only DRAFT components can be published');
                        break;
                    }
                    setResourceToPublish(item);
                    setPublishModalOpen(true);
                } else if (item.type === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS && item.object) {
                    // Check if descriptor can be published (must be in DRAFT state)
                    if (item.object.descriptorState !== 'DRAFT') {
                        toast.error('Only DRAFT descriptors can be published');
                        break;
                    }
                    
                    OccurrenceReportDescriptorService.publishDescriptor(item.object.id).then((descriptor) => {
                        // Update the current object if it's open
                        if (currentWorkspaceObject?.object?.id === descriptor.id) {
                            setCurrentWorkspaceObject({ 
                                objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS, 
                                object: descriptor 
                            });
                        }
                        // Refresh the list
                        setWorkspaceObjects({ objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS });
                        toast.success(`Descriptor "${item.name}" published successfully`);
                    }).catch((error) => {
                        toast.error(`Error publishing descriptor: ${error.message}`);
                    });
                }
                break;
            case 'run':
                if (item.type === ResourceType.INTEGRATED_RISK_PICTURES && item.object) {
                    IRPService.runIntegratedRiskPicture(parseInt(item.object.id)).then(() => {
                        toast.success(`IRP run started for item: ${item.object.id}`);
                        // Refresh the explorer after 1 second to show the updated status
                        setTimeout(() => {
                            if (explorerRef.current) {
                                explorerRef.current.refresh();
                            }
                        }, 1000);
                    }).catch((error) => {
                        toast.error(`Error starting IRP run: ${error.message}`);
                    });
                }
                break;

            case 'syncChanged':
                startRecommendationJob(item, 'changed');
                break;

            case 'syncOpen':
                startRecommendationJob(item, 'open');
                break;

            case 'syncFull':
                startRecommendationJob(item, 'full');
                break;

            case 'rebuild':
                startRecommendationJob(item, 'rebuild');
                break;

            case 'reindex':
                startRecommendationJob(item, 'reindex');
                break;

            case 'taxonomyTag':
                startRecommendationJob(item, 'taxonomyTag');
                break;

            case 'taxonomyRetag':
                startRecommendationJob(item, 'taxonomyRetag');
                break;

            case 'restore':
                if (item.type === ResourceType.INTEGRATED_RISK_PICTURES && item.object) {
                    IRPService.restoreIRP(parseInt(item.object.id)).then(() => {
                        toast.success(`IRP "${item.object.name}" restored successfully`);
                        // Refresh the explorer to update the list
                        if (explorerRef.current) {
                            explorerRef.current.refresh();
                        }
                    }).catch((error) => {
                        toast.error(`Error restoring IRP: ${error.message}`);
                    });
                }
                break;

            case 'hardDelete':
                setResourceToHardDelete(item);
                setHardDeleteModalOpen(true);
                break;

            case 'export':
                if (item.type === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS) {
                    handleExportDescriptor(item);
                }
                break;

            case 'importMapping':
                if (item.type === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS && item.object?.id) {
                    setImportMappingTarget(item);
                    if (importMappingInputRef.current) {
                        importMappingInputRef.current.value = '';
                        importMappingInputRef.current.click();
                    }
                }
                break;

            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown action: ${action}`);
        }
    };

    /** Publishes the draft the "Publish" action opened {@link PublishComponent} for. */
    const handlePublishConfirm = (releaseLevel: ReleaseLevel, releaseNotes: string): void => {
        const componentId = resourceToPublish?.object?.id;
        if (!componentId) {
            return;
        }
        setPublishing(true);
        componentService.applyLifecycleTransition(componentId, ComponentLifecycleTransition.PUBLISH,
            releaseLevel, releaseNotes)
            .then((saved) => {
                publishResourceUpdate({
                    type: ResourceType.COMPONENT,
                    id: saved.id,
                    updatedData: saved,
                    source: 'save',
                });
                toast.success(`Published v${saved.version} of ${saved.name}`);
                setPublishModalOpen(false);
                setResourceToPublish(null);
            })
            .catch((error) => {
                toast.error('Error publishing component: ' + error.message);
            })
            .finally(() => setPublishing(false));
    };

    // Handler for create button
    const handleCreateClick = (): void => {
        if (workspaceObjects?.objectType) {
            addOrSelectResource(new ResourceBase(workspaceObjects.objectType));
        }
    };

    // Export a descriptor to a JSON file download
    const handleExportDescriptor = (item: ExtendedTreeViewItem): void => {
        const descriptor = item.object;
        if (!descriptor) return;

        const exportData = {
            isamDescriptorExport: '1.0',
            exportedAt: new Date().toISOString(),
            descriptor: {
                name: descriptor.name || '',
                description: descriptor.description || '',
                uri: descriptor.uri || '',
                descriptorTerms: (descriptor.descriptorTerms || []).map((t: any) => ({
                    term: t.term,
                    definition: t.definition,
                })),
            },
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const safeName = (descriptor.name || 'descriptor').replace(/[^a-z0-9_-]/gi, '_');
        anchor.href = url;
        anchor.download = `${safeName}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    // Open the file picker for descriptor import
    const handleImportDescriptorClick = (): void => {
        if (importDescriptorInputRef.current) {
            importDescriptorInputRef.current.value = '';
            importDescriptorInputRef.current.click();
        }
    };

    // Process a selected mapping import file for a descriptor
    const handleImportMappingFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (!file || !importMappingTarget?.object?.id) return;
        const descriptorId: number = importMappingTarget.object.id;
        const descriptorName: string = importMappingTarget.name || 'descriptor';
        setImportMappingTarget(null);
        OccurrenceReportDescriptorService.importMappingData(descriptorId, file)
            .then((result) => {
                toast.success(`Import complete for "${descriptorName}": ${result.mapped} mapped, ${result.skipped} skipped, ${result.failed} failed`);
                publishResourceUpdate({
                    type: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
                    id: descriptorId,
                    source: 'refresh',
                    updatedData: null
                });
            })
            .catch((error: unknown) => {
                const msg = error instanceof Error ? error.message : 'Failed to import mapping data';
                toast.error(msg);
            });
    };

    // Process a selected JSON file and open a pre-populated new descriptor tab
    const handleImportDescriptorFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e): void => {
            try {
                const parsed = JSON.parse(e.target?.result as string);

                // Validate the export format
                if (!parsed.isamDescriptorExport || !parsed.descriptor) {
                    toast.error('Invalid descriptor export file. The file does not appear to be an ISAM descriptor export.');
                    return;
                }

                const src = parsed.descriptor;
                if (!src.name || !src.description) {
                    toast.error('Invalid descriptor export file. Missing required fields (name, description).');
                    return;
                }

                // Auto-suffix the name to reduce naming conflicts
                const MAX_NAME_LENGTH = 255;
                const SUFFIX = ' (imported)';
                const baseName: string = src.name.length + SUFFIX.length > MAX_NAME_LENGTH
                    ? src.name.substring(0, MAX_NAME_LENGTH - SUFFIX.length)
                    : src.name;
                const importedName = baseName + SUFFIX;

                const importedDescriptor = {
                    name: importedName,
                    description: src.description || '',
                    uri: src.uri || '',
                    descriptorState: 'DRAFT',
                    descriptorTerms: (src.descriptorTerms || []).map((t: any) => ({
                        term: t.term || '',
                        definition: t.definition || '',
                    })),
                    group: { id: 0 },
                    breadcrumb: [],
                };

                setCurrentWorkspaceObject({
                    objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
                    object: importedDescriptor,
                });
                toast.info(`Descriptor "${importedName}" loaded. Edit and save to create it.`);
            } catch {
                toast.error('Failed to parse the import file. Please ensure it is a valid JSON file.');
            }
        };
        reader.readAsText(file);
    };

    // Handler for IRP Workbook button
    const handleOpenIRPWorkbook = (): void => {
        // Create a demo workbook resource
        const workbookResource = {
            id: 999999, // Demo ID
            name: 'IRP Workbook - Demo'
        };
        
        setCurrentWorkspaceObject({
            objectType: ResourceType.IRP_WORKBOOK,
            object: workbookResource
        });
    };

    // Note: Authentication is handled by ProtectedRoute at the routing level.
    // If a 401 occurs during component usage, it means the session has expired
    // and the user should refresh the page to trigger ProtectedRoute's auth check.

    // Set role="separator" on the resizer element so that it is accessible
    React.useEffect(() => {
        // get html element with attribute role="Resizer"
        const resizers = document.querySelectorAll('[role="Resizer"]');
        if (resizers && resizers.length > 0) {
            resizers.forEach((resizer) => {
                // change role to a valid aria role
                resizer.setAttribute('role', 'separator');
            });
        }
    }, []);

    // Initialization effect - capture URL action early
    React.useEffect(() => {
        const urlAction = getUrlAction();
        if (urlAction && urlAction.action === 'openModel') {
            setPendingUrlAction(urlAction);
        }
    }, []); // Only run once on mount

    // Process initialization and URL actions after groups are loaded
    React.useEffect(() => {
        const groups = additionalResources.find(r => r.objectType === ResourceType.GROUPS);
                
        // Wait for groups to be loaded
        if (loadingGroups || !groups) {
            return;
        }

        // Mark as initialized once groups are loaded
        if (!isInitialized) {
            setIsInitialized(true);
            
            // Process pending URL action or switch to default tab
            if (pendingUrlAction) {
                // For event sequence trees, we need to switch to the Safety Models tab
                let tabIndex: number;
                if (pendingUrlAction.type === ResourceType.EVENT_SEQUENCE_TREES) {
                    tabIndex = getWorkspaceObjectTypeAsNumber(ResourceType.SAFETY_MODELS);
                } else {
                    tabIndex = getWorkspaceObjectTypeAsNumber(pendingUrlAction.type);
                }
                
                onTabClick(tabIndex);
            } else {
                // calling onTabClick(1) causes the issue 1436 reported by Jackie.

                // No URL action, switch to default tab (Safety Models)
                // onTabClick(1);
            }
        }
    }, [loadingGroups, additionalResources, isInitialized, pendingUrlAction]);

    // Connect to SSE server to listen for duplicateGroup, deleteGroup and duplicateSafetyModel events
    // Other events can be added here as needed
    React.useEffect(() => {
        const handleSSEvent = (resourceType: ResourceType, event: any): void => {
            const data = JSON.parse(event.data);
            if (data.status === JOB_STATUS_TYPES.completed) {
                toast.success(data.name + ' completed successfully');
                setWorkspaceObjects({ objectType: resourceType });
            }
        }

        // Group delete needs extra handling: close any tabs the user may have opened
        // during the async window between "job enqueued" (API return) and "job completed"
        // (SSE event). The pending group info was registered in Group.actions.menu.tsx.
        const handleGroupDeleteSSEvent = (event: any): void => {
            const data = JSON.parse(event.data);
            if (data.status === JOB_STATUS_TYPES.completed) {
                toast.success(data.name + ' completed successfully');
                setWorkspaceObjects({ objectType: ResourceType.GROUPS });

                // data.name = "Deleting group {groupName}" — strip prefix to recover group name
                const groupName = (data.name as string).replace(/^Deleting group /, '');
                const pending = consumePendingGroupDelete(groupName);
                if (pending) {
                    setDeletedWorkspaceObject({
                        objectType: ResourceType.GROUPS,
                        id: pending.id,
                        children: pending.children
                    });
                }
            }
        }

        const closeSSEConnection = (): void => {
            if (sseConnectionRef.current) {
                sseConnectionRef.current.removeEventListener(SSE_EVENT_TYPES.duplicateGroup,
                    handleSSEvent.bind(null, ResourceType.GROUPS));
                sseConnectionRef.current.removeEventListener(SSE_EVENT_TYPES.deleteGroup,
                    handleGroupDeleteSSEvent);
                sseConnectionRef.current.removeEventListener(SSE_EVENT_TYPES.duplicateSafetyModel,
                    handleSSEvent.bind(null, ResourceType.SAFETY_MODELS));
                sseConnectionRef.current.close();
            }
        }

        if (sseConnectionRef.current) {
            closeSSEConnection();
        }

        sseConnectionRef.current = new EventSource(API_URL + 'sse', { withCredentials: true });

        sseConnectionRef.current.addEventListener(SSE_EVENT_TYPES.duplicateGroup,
            handleSSEvent.bind(null, ResourceType.GROUPS));
        sseConnectionRef.current.addEventListener(SSE_EVENT_TYPES.deleteGroup,
            handleGroupDeleteSSEvent);
        sseConnectionRef.current.addEventListener(SSE_EVENT_TYPES.duplicateSafetyModel,
            handleSSEvent.bind(null, ResourceType.SAFETY_MODELS));


        sseConnectionRef.current.onopen = (): void => {
        }

        sseConnectionRef.current.onerror = (error: any): void => {
            toast.error(error.message);
        }
        
        return (): void => {
            if (sseConnectionRef.current) {
                closeSSEConnection();
            }
        }
    }, []);


    React.useEffect(() => {
        
        PhasesOfFlightService.getPhasesOfFlight()
            .then((phasesOfFlight: PhaseOfFlight[]) => {
                setPhasesOfFlight(phasesOfFlight);
            })
            .catch((error: Error) => {
                toast.error(error.message);
            });
        
    }, [setPhasesOfFlight]);

    React.useEffect(() => {
        occurrenceCategoriesService.getOccurrenceCategories().then((data: OccurrenceCategory[]) => {
            setOccurrenceCategories(data);
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    }, [setOccurrenceCategories]);

    React.useEffect(() => {
        PedigreesService.getPedigrees().then((data: string[]) => {
            setPedigrees(data);
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    }, [setPedigrees]);

    React.useEffect(() => {
        OccurrenceService.getOccurrenceTypes().then((data: string[]) => {
            setOccurrenceTypes(data);
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    }, [setOccurrenceTypes]);

    const loadObjects = (setLoadingObjects: CallableFunction, page: BasicPage, groupId?: number, searchTerm?: string, startSearchDate?: Moment | null, endSearchDate?: Moment | null, selectedDescriptorType?: number): void => {
        let type = workspaceObjects?.objectType ?? ResourceType.GROUPS;
        if (!additionalResources.find(value => value.objectType === type) && (type == ResourceType.GROUPS || type == ResourceType.SAFETY_MODELS)) {
            const fetchAllObjectsPromise: Promise<any> | null = fetchAllResources(type);
            if (fetchAllObjectsPromise) {
                setLoadingObjects(true);
                if (type === ResourceType.GROUPS) {
                    setLoadingGroups(true);
                }
                fetchAllObjectsPromise.then((allObjects) => {
                    addOrUpdateAdditionalResource({ objectType: type, resources: allObjects });
                    if (type === ResourceType.GROUPS && getWorkspaceObjectTypeAsNumber(type) === tabIndexRef.current) {
                        const pagedGroups = getResourcesAsPage(allObjects, page, groupId, searchTerm);
                        setWorkspaceObjects({ objectType: type, resources: pagedGroups.content, page: pagedGroups.page });
                    }
                    
                    // Check if we need to process a pending URL action after loading safety models
                    if (type === ResourceType.SAFETY_MODELS && pendingUrlAction && 
                        (pendingUrlAction.type === ResourceType.SAFETY_MODELS || 
                         pendingUrlAction.type === ResourceType.EVENT_SEQUENCE_TREES)) {
                        processPendingUrlAction();
                    }
                }).catch((error) => {
                    toast.error(error.message);
                }).finally(() => {
                    setLoadingObjects(false);
                    if (type === ResourceType.GROUPS) {
                        setLoadingGroups(false);
                    }
                });
            }
        } else if (type === ResourceType.GROUPS) {
            const resources = getAdditionalResource(type);
            if (resources) {
                const pagedGroups = getResourcesAsPage(resources.resources, page, groupId, searchTerm);
                setWorkspaceObjects({objectType: type, resources: pagedGroups.content, page: pagedGroups.page});
            }
        }
        if (type !== ResourceType.GROUPS) {
            const fetchObjectsPromise: Promise<any> | null = fetchPagedWorkspaceObjects(type, page ?? defaultPageLarge, groupId, searchTerm, getAdditionalResource(ResourceType.GROUPS).resources, startSearchDate, endSearchDate, selectedDescriptorType);
            if (fetchObjectsPromise) {
                setLoadingObjects(true);
                fetchObjectsPromise.then((objects) => {
                    // Prevent setting workspace objects if the tab index has changed during the loading of the objects.
                    // This can happen if the user clicks on a different tab while the objects are being fetched which leads to
                    // the workspace objects being loaded into the wrong tab.
                    if (getWorkspaceObjectTypeAsNumber(type) === tabIndexRef.current) {
                        if (objects.page && objects.content) {
                            setWorkspaceObjects({
                                objectType: type, resources: objects.content,
                                page: {
                                    number: objects.page.number,
                                    totalElements: objects.page.totalElements,
                                    size: objects.page.size,
                                    totalPages: objects.page.totalPages
                                }
                            });
                        } else {
                            setWorkspaceObjects({objectType: type, resources: objects});
                        }
                        
                        // Check if we need to process a pending URL action after loading resources
                        if (pendingUrlAction && type === pendingUrlAction.type) {
                            processPendingUrlAction();
                        }
                    }
                }).catch((error) => {
                    toast.error(error.message);
                }).finally(() => {
                    setLoadingObjects(false);
                });
            }
        }
    }

    const onBot = (): void => {
        let newSizePercent = [...panelSizePercent];
        if (panelSizePercent[2] === 0) {
            newSizePercent[2] = '26%';
        } else {
            newSizePercent[2] = 0;
        }
        setPanelSizePercent(newSizePercent);
    };

    const collapseExpandAllSashes = (): void => {
        let newSizePercent = [...panelSizePercent];
        if (panelSizePercent[0] === 0) {
            newSizePercent[0] = '24%';
        } else {
            newSizePercent[0] = 0;
        }
        setPanelSizePercent(newSizePercent);
    };

    const collapseExpandSashUsingIndex = (index: number): void => {
        let newSizePercent = [...panelSizePercent];
        if (index === 0) {
            if (panelSizePercent[0] === 0) {
                newSizePercent[0] = '24%';
            } else {
                newSizePercent[0] = 0;
            }
        }
        setPanelSizePercent(newSizePercent);
    };

    // verticalMenuItems removed - now handled by the new Explorer's dataset dropdown

    const onObjectChange = (objectToChange: {objectType: ResourceType, object: any}): void => {
        setCurrentWorkspaceObject(objectToChange);
    }

    const addOrSelectResource = (resource: ResourceBase): void => {
        if (resource.id || resource.uniqueId) {
            const groups = getAdditionalResource(ResourceType.GROUPS).resources;

            const fetchPromise = fetchWorkspaceObject(resource, groups);
            
            if (fetchPromise) {
                fetchPromise.then((object) => {
                    setCurrentWorkspaceObject({objectType: resource.type, object: object});
                }).catch((error) => {
                    // Handle 401 errors specially - don't log out the user
                    if (error.response?.status === 401 || error.message?.includes('401')) {
                        // User doesn't have permission to view this resource
                        toast.warning(`You don't have permission to view this ${resource.type.toLowerCase()}`);
                        // Don't propagate the error further to prevent logout
                    } else {
                        // Other errors - show them normally
                        toast.error(error.message);
                    }
                });
            } else {
                // fetchWorkspaceObject returned null/undefined
                toast.error(`Unable to fetch ${resource.type} with ID ${resource.id}`);
            }
        } else {
            const newObject = createWorkspaceObject(resource);
            setCurrentWorkspaceObject({objectType: resource.type, object: newObject });
        }
    }
    
    // Authentication is handled by ProtectedRoute - we can render immediately
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', ...dimension, ...aria }}>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loadingGroups}> 
                <CircularProgress color='inherit' />
            </Backdrop>

            <WorkspaceHeader onBot={onBot} onSash={collapseExpandAllSashes} onTabClick={onTabClick} addOrSelectResource={addOrSelectResource}
                loadObjects={loadObjects} />

            <SplitPane
                split='vertical'
                sizes={panelSizePercent}
                onChange={(sizes: number[]): void => (setPanelSizePercent(sizes))}
                sashRender={(index: number, active: boolean): React.ReactNode => {
                    const isCollapsed = panelSizePercent[index] === 0;
                    return (
                        <SashContent
                            className={`action-sash-wrap ${active ? 'active' : 'hidden-sash'} ${isCollapsed ? 'collapsed' : ''}`}
                        >
                            <span 
                                className='action' 
                                onClick={(): void => { collapseExpandSashUsingIndex(index) }}
                                title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
                                aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
                            >
                                {isCollapsed ? 
                                    <ArrowRightIcon sx={{ fontSize: 14 }} /> : 
                                    <ArrowLeftIcon sx={{ fontSize: 14 }} />
                                }
                            </span>
                            <span/>
                        </SashContent>
                    );
                }}
            >
                {/* New Explorer that combines menu and tree */}
                <Pane style={{borderRight: '1px solid rgba(0,0,0,0.12)'}}>
                    <NewExplorerContainer
                        ref={explorerRef}
                        workspaceId="1" // There is only ever one workspace in the current implementation
                        currentDataset={workspaceObjects?.objectType as ExplorerDatasetType}
                        onDatasetChange={handleDatasetChange}
                        onResourceSelect={(item: ExtendedTreeViewItem) : void => {
                            // Single-click on an Event Sequence opens its diagram; on a Component, its fault tree.
                            const openTree = item.type === ResourceType.EVENT_SEQUENCES
                                || item.type === ResourceType.COMPONENT;
                            handleResourceSelect(item, openTree);
                        }}
                        onResourceDoubleClick={handleResourceDoubleClick}
                        onEdit={handleResourceSelect}
                        onDelete={(item: ExtendedTreeViewItem) : void => handleAction('delete', item)}
                        onCopy={(item: ExtendedTreeViewItem) : void => handleAction('duplicate', item)}
                        onAction={handleAction}
                        onOpenIRPWorkbook={handleOpenIRPWorkbook}
                        toolbar={{
                            showCreateButton: workspaceObjects?.objectType ? canCreateNewItem(workspaceObjects.objectType as ExplorerDatasetType) : false,
                            onCreateClick: handleCreateClick,
                            createButtonText: workspaceObjects?.objectType ? getCreateButtonLabel(workspaceObjects.objectType as ExplorerDatasetType) : 'New',
                            // Components live in the Safety Models dataset and belong to a working
                            // area, so Create there offers both rather than making a model only.
                            createOptions: workspaceObjects?.objectType === ResourceType.SAFETY_MODELS ? [
                                { label: 'Safety Model', onClick: handleCreateClick },
                                { label: 'Component', onClick: (): void => addOrSelectResource(new ResourceBase(ResourceType.COMPONENT)) },
                            ] : undefined,
                            showImportButton: workspaceObjects?.objectType === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
                            onImportClick: handleImportDescriptorClick,
                        }}
                    />
                </Pane>
                
                {/* TabEditor */}
                <Pane style={{marginLeft: '1px'}}>
                    <TabEditor objectToEdit={currentWorkspaceObject} onTabChange={onObjectChange} />
                </Pane>
                
                {/* ChatBotEditor */}
                <Pane style={{marginLeft: '1px'}}>
                    <ChatBotEditor panelSizePercent={panelSizePercent} />
                </Pane>

            </SplitPane>

            {/* Hidden file input for descriptor import */}
            <input
                ref={importDescriptorInputRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleImportDescriptorFile}
            />

            {/* Hidden file input for mapping import */}
            <input
                ref={importMappingInputRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleImportMappingFile}
            />

            {/* Delete Modal */}
            {resourceToDelete && (
                <DeleteResource
                    open={deleteModalOpen}
                    onClose={(): void => {
                        if (!isDeleting) {
                            setDeleteModalOpen(false);
                            setResourceToDelete(null);
                            setIsDeleting(false);
                            setDeletionProgressMessage(undefined);
                        }
                    }}
                    resourceType={resourceToDelete.type}
                    resourceName={resourceToDelete.name}
                    subResources={getSubResourceOnDelete(resourceToDelete.type)}
                    additionalWarning={((): string | undefined => {
                        if (resourceToDelete.type === ResourceType.INTEGRATED_RISK_PICTURES && resourceToDelete.object) {
                            const status = resourceToDelete.object.status;
                            switch (status) {
                                case 'QUEUED':
                                case 'RUNNING':
                                    return 'This will cancel the currently running job. The cancellation may take up to 30 seconds.';
                                case 'COMPLETED':
                                    return authService.isSuperUser()
                                        ? 'This will permanently delete the IRP and all its results. This action cannot be undone.'
                                        : 'This will permanently archive the IRP. The results will no longer be available.';
                                default:
                                    return undefined;
                            }
                        }
                        return undefined;
                    })()}
                    actionLabel={
                        resourceToDelete.type === ResourceType.INTEGRATED_RISK_PICTURES &&
                        resourceToDelete.object?.status === 'COMPLETED' && !authService.isSuperUser() ? 'Archive' : 'Delete'
                    }
                    isDeleting={isDeleting}
                    progressMessage={deletionProgressMessage}
                    handleDelete={(): void => {
                        if (resourceToDelete.object) {
                            let deletePromise: Promise<any>;
                            
                            switch (resourceToDelete.type) {
                                case ResourceType.GROUPS: {
                                    const childrenIds = getTreeItemDescendantIds(resourceToDelete.object,
                                        ResourceType.GROUPS,
                                        workspaceObjects?.resources ?? []);
                                    deletePromise = GroupService.deleteGroupById(resourceToDelete.object.id!).then(() => {
                                        setDeletedWorkspaceObject({
                                            objectType: ResourceType.GROUPS,
                                            id: resourceToDelete.object.id!,
                                            children: childrenIds
                                        });
                                        setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS });
                                    });
                                    break;
                                }
                                
                                case ResourceType.SAFETY_MODELS: {
                                    const childrenIds = resourceToDelete.object.children?.map((child: any) => child.id) ?? [];
                                    deletePromise = safetyService.deleteSafetyModelById(resourceToDelete.object.id).then(() => {
                                        setDeletedWorkspaceObject({
                                            objectType: ResourceType.SAFETY_MODELS,
                                            id: resourceToDelete.object.id,
                                            children: childrenIds
                                        });
                                        setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
                                    });
                                    break;
                                }
                                    
                                case ResourceType.EVENT_SEQUENCES: {
                                    // Store current object in case we need to restore on error
                                    const previousWorkspaceObject = currentWorkspaceObject;
                                    
                                    // Clear currentWorkspaceObject first if it's the one being deleted
                                    if (currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCES && 
                                        currentWorkspaceObject?.object?.id === resourceToDelete.object.id) {
                                        setCurrentWorkspaceObject(null);
                                    }
                                    
                                    deletePromise = eventSequenceService.deleteEventSequence(resourceToDelete.object.id)
                                        .then(() => {
                                            // Notify tab editor about deletion
                                            setDeletedWorkspaceObject({
                                                objectType: ResourceType.EVENT_SEQUENCES,
                                                id: resourceToDelete.object.id,
                                                children: []
                                            });
                                            
                                            // Publish update event so explorer and other components refresh
                                            publishResourceUpdate({
                                                type: ResourceType.EVENT_SEQUENCES,
                                                id: resourceToDelete.object.id,
                                                source: 'delete',
                                                updatedData: null
                                            });
                                            
                                            // Refresh safety models to update the tree
                                            setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
                                        })
                                        .catch((error) => {
                                            // Restore previous workspace object if we cleared it
                                            if (previousWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCES && 
                                                previousWorkspaceObject?.object?.id === resourceToDelete.object.id) {
                                                setCurrentWorkspaceObject(previousWorkspaceObject);
                                            }
                                            throw error; // Re-throw to be handled by the outer catch
                                        });
                                    break;
                                }
                                    
                                case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
                                    // eslint-disable-next-line no-console
                                    // console.log('Deleting occurrence report descriptor:', resourceToDelete.object.id);
                                    deletePromise = OccurrenceReportDescriptorService.deleteDescriptorById(resourceToDelete.object.id).then(() => {
                                        // eslint-disable-next-line no-console
                                        // console.log('Delete successful, notifying tab editor');
                                        
                                        // Clear currentWorkspaceObject if it's the one being deleted
                                        if (currentWorkspaceObject?.objectType === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS && 
                                            currentWorkspaceObject?.object?.id === resourceToDelete.object.id) {
                                            setCurrentWorkspaceObject(null);
                                        }
                                        
                                        setDeletedWorkspaceObject({
                                            objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
                                            id: resourceToDelete.object.id
                                        });
                                        
                                        // Publish delete event to notify explorer
                                        // eslint-disable-next-line no-console
                                        // console.log('Publishing delete event');
                                        publishResourceUpdate({
                                            type: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
                                            id: resourceToDelete.object.id.toString(),
                                            source: 'delete',
                                            updatedData: null
                                        });
                                        
                                        setWorkspaceObjects({ objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS });
                                    });
                                    break;
                                    
                                case ResourceType.INTEGRATED_RISK_PICTURES: {
                                    const irpStatus = resourceToDelete.object.status;
                                    const irpId = resourceToDelete.object.id;
                                    const isSuperUserHardDelete = irpStatus === 'COMPLETED' && authService.isSuperUser();
                                    if (irpStatus === 'QUEUED' || irpStatus === 'RUNNING') {
                                        setIsDeleting(true);
                                        setDeletionProgressMessage('Cancelling job...');
                                    }

                                    // For super users on COMPLETED IRPs: archive then hard-delete
                                    // For everyone else: standard delete (which archives COMPLETED IRPs)
                                    let deleteChain: Promise<void> = IRPService.deleteIntegratedRiskPicture(irpId);
                                    if (isSuperUserHardDelete) {
                                        deleteChain = deleteChain.then(() => IRPService.hardDeleteIRP(parseInt(irpId)));
                                    }

                                    deletePromise = deleteChain.then(() => {
                                        // Clear currentWorkspaceObject if it's the one being deleted
                                        if (currentWorkspaceObject?.objectType === ResourceType.INTEGRATED_RISK_PICTURES &&
                                            currentWorkspaceObject?.object?.id === irpId) {
                                            setCurrentWorkspaceObject(null);
                                        }

                                        setDeletedWorkspaceObject({
                                            objectType: ResourceType.INTEGRATED_RISK_PICTURES,
                                            id: irpId
                                        });

                                        // Publish delete event to notify explorer
                                        publishResourceUpdate({
                                            type: ResourceType.INTEGRATED_RISK_PICTURES,
                                            id: irpId.toString(),
                                            source: 'delete',
                                            updatedData: null
                                        });

                                        setWorkspaceObjects({ objectType: ResourceType.INTEGRATED_RISK_PICTURES });
                                    });
                                    break;
                                }

                                case ResourceType.USERS: {
                                    deletePromise = UserService.deleteUserById(resourceToDelete.object.id!).then(() => {
                                        setDeletedWorkspaceObject({
                                            objectType: ResourceType.USERS,
                                            id: resourceToDelete.object.id!
                                        });

                                        publishResourceUpdate({
                                            type: ResourceType.USERS,
                                            id: resourceToDelete.object.id.toString(),
                                            source: 'delete',
                                            updatedData: null
                                        });

                                        setWorkspaceObjects({ objectType: ResourceType.USERS });
                                    });
                                    break;
                                }

                                case ResourceType.COMPONENT: {
                                    // Only DRAFT Components can be deleted; the server enforces this
                                    // and returns 400 otherwise (surfaced by the outer catch).
                                    deletePromise = componentService.deleteComponent(resourceToDelete.object.id).then(() => {
                                        // Clear currentWorkspaceObject if it's the one being deleted
                                        if (currentWorkspaceObject?.objectType === ResourceType.COMPONENT &&
                                            currentWorkspaceObject?.object?.id === resourceToDelete.object.id) {
                                            setCurrentWorkspaceObject(null);
                                        }

                                        setDeletedWorkspaceObject({
                                            objectType: ResourceType.COMPONENT,
                                            id: resourceToDelete.object.id
                                        });

                                        publishResourceUpdate({
                                            type: ResourceType.COMPONENT,
                                            id: resourceToDelete.object.id.toString(),
                                            source: 'delete',
                                            updatedData: null
                                        });

                                        setWorkspaceObjects({ objectType: ResourceType.COMPONENT });
                                    });
                                    break;
                                }

                                default:
                                    toast.error(`Delete not implemented for ${resourceToDelete.type}`);
                                    setResourceToDelete(null);
                                    return;
                            }
                            
                            deletePromise.then(() => {
                                // Special handling for IRP archive vs delete
                                const action = resourceToDelete.type === ResourceType.INTEGRATED_RISK_PICTURES &&
                                    resourceToDelete.object?.status === 'COMPLETED' && !authService.isSuperUser() ? 'archived' : 'deleted';
                                toast.success(`"${resourceToDelete.name}" ${action} successfully`);
                            }).catch((error) => {
                                // Handle specific error codes for IRPs
                                if (resourceToDelete.type === ResourceType.INTEGRATED_RISK_PICTURES && error.response) {
                                    const status = error.response.status;
                                    const resourceName = resourceToDelete.name;
                                    
                                    switch (status) {
                                        case 403:
                                            toast.error(`Permission denied: You don't have permission to delete "${resourceName}"`);
                                            break;
                                        case 404:
                                            toast.error(`Not found: "${resourceName}" no longer exists`);
                                            // Refresh the list since the resource doesn't exist
                                            setWorkspaceObjects({ objectType: ResourceType.INTEGRATED_RISK_PICTURES });
                                            break;
                                        case 409:
                                            toast.error(`Conflict: "${resourceName}" was modified by another user. Please refresh and try again`);
                                            break;
                                        default:
                                            toast.error(`Error deleting ${resourceName} - ${error.message}`);
                                    }
                                } else {
                                    // Generic error handling for other resource types
                                    toast.error(`Error deleting ${resourceToDelete.name} - ${error.message}`);
                                }
                            }).finally(() => {
                                setIsDeleting(false);
                                setDeletionProgressMessage(undefined);
                                setResourceToDelete(null);
                            });
                        }
                    }}
                />
            )}

            {/* Hard Delete Modal */}
            {resourceToHardDelete && (
                <DeleteResource
                    open={hardDeleteModalOpen}
                    onClose={(): void => {
                        if (!isDeleting) {
                            setHardDeleteModalOpen(false);
                            setResourceToHardDelete(null);
                            setIsDeleting(false);
                            setDeletionProgressMessage(undefined);
                        }
                    }}
                    resourceType={resourceToHardDelete.type}
                    resourceName={resourceToHardDelete.name}
                    subResources="all associated data"
                    additionalWarning="This action is PERMANENT and cannot be undone. The IRP will be completely removed from the database."
                    actionLabel="Permanently Delete"
                    isDeleting={isDeleting}
                    progressMessage={deletionProgressMessage}
                    handleDelete={(): void => {
                        if (resourceToHardDelete.object) {
                            setIsDeleting(true);
                            setDeletionProgressMessage('Permanently deleting...');

                            IRPService.hardDeleteIRP(parseInt(resourceToHardDelete.object.id))
                                .then(() => {
                                    // Close the workspace editor if showing this IRP
                                    if (currentWorkspaceObject?.objectType === ResourceType.INTEGRATED_RISK_PICTURES &&
                                        currentWorkspaceObject?.object?.id === resourceToHardDelete.object.id) {
                                        setCurrentWorkspaceObject(null);
                                    }

                                    // Remove from the tree
                                    setDeletedWorkspaceObject({
                                        objectType: ResourceType.INTEGRATED_RISK_PICTURES,
                                        id: resourceToHardDelete.object.id
                                    });

                                    // Publish delete event to notify explorer
                                    publishResourceUpdate({
                                        type: ResourceType.INTEGRATED_RISK_PICTURES,
                                        id: resourceToHardDelete.object.id.toString(),
                                        source: 'delete',
                                        updatedData: null
                                    });

                                    // Refresh workspace objects
                                    setWorkspaceObjects({ objectType: ResourceType.INTEGRATED_RISK_PICTURES });

                                    // Refresh the explorer
                                    if (explorerRef.current) {
                                        explorerRef.current.refresh();
                                    }

                                    toast.success(`"${resourceToHardDelete.name}" permanently deleted`);
                                })
                                .catch((error) => {
                                    toast.error(`Error permanently deleting IRP: ${error.message}`);
                                })
                                .finally(() => {
                                    setIsDeleting(false);
                                    setDeletionProgressMessage(undefined);
                                    setHardDeleteModalOpen(false);
                                    setResourceToHardDelete(null);
                                });
                        }
                    }}
                />
            )}

            {/* Duplicate Modal */}
            {resourceToDuplicate && (
                <DuplicateResource
                    open={duplicateModalOpen}
                    title={`Duplicate ${resourceToDuplicate.type === ResourceType.GROUPS ? 'Working Area' :
                        resourceToDuplicate.type === ResourceType.EVENT_SEQUENCES ? 'Safety View' :
                            'Resource'} - ${resourceToDuplicate.name}`}
                    onClose={(): void => {
                        setDuplicateModalOpen(false);
                        setResourceToDuplicate(null);
                    }}
                    onConfirm={(destinationId: number): void => {
                        setDuplicateModalOpen(false);
                        if (resourceToDuplicate.object) {
                            let duplicatePromise: Promise<any> | undefined
                            
                            switch (resourceToDuplicate.type) {
                                case ResourceType.GROUPS: {
                                    const targetGroupId = destinationId > 0 ? destinationId : undefined;
                                    const sourceGroupId = resourceToDuplicate.object.id;
                                    duplicatePromise = GroupService.duplicateGroup(sourceGroupId!, targetGroupId).then((response) => {
                                        toast.info(response.data.status + ' ' + response.data.name);
                                    });
                                    break;
                                }
                                
                                case ResourceType.SAFETY_MODELS: {
                                    const targetGroupId = destinationId > 0 ? destinationId : undefined;
                                    const sourceResourceId = resourceToDuplicate.object.id;
                                    duplicatePromise = safetyService.duplicateSafetyModel(sourceResourceId, targetGroupId).then((response) => {
                                        toast.info(response.data.status + ' ' + response.data.name);
                                    }).catch((error) => {
                                        toast.error('Error duplicating safety model - ' + error.message);
                                    });
                                    break;
                                }
                                    
                                case ResourceType.EVENT_SEQUENCES:
                                    duplicatePromise = eventSequenceService.duplicateEventSequence(
                                        resourceToDuplicate.object.id, 
                                        destinationId
                                    ).then(() => {
                                        setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
                                    });
                                    break;

                                default:
                                    toast.error(`Duplicate not implemented for ${resourceToDuplicate.type}`);
                                    setResourceToDuplicate(null);
                                    return;
                            }
                            if (duplicatePromise) {
                                duplicatePromise.then(() => {
                                    if (resourceToDuplicate.type !== ResourceType.GROUPS) {
                                        toast.success(`"${resourceToDuplicate.name}" duplicated successfully`);
                                    }
                                }).catch((error) => {
                                    toast.error(`Error duplicating ${resourceToDuplicate.name} - ${error.message}`);
                                }).finally(() => {
                                    setResourceToDuplicate(null);
                                });
                            }
                        }
                    }}
                    object={{objectType: resourceToDuplicate.type, object: resourceToDuplicate.object}}
                    data={
                        resourceToDuplicate.type === ResourceType.GROUPS ?
                            excludeGroupAndDescendants(getAdditionalResource(ResourceType.GROUPS), resourceToDuplicate.object, workspaceObjects) :
                            resourceToDuplicate.type === ResourceType.SAFETY_MODELS ?
                                filterOnGroupCanEditResources(getAdditionalResource(ResourceType.GROUPS)) :
                                resourceToDuplicate.type === ResourceType.EVENT_SEQUENCES ?
                                    filterOnGroupCanEditResources(getAdditionalResource(ResourceType.SAFETY_MODELS)) :
                                    resourceToDuplicate.type === ResourceType.INTEGRATED_RISK_PICTURES ?
                                        getAdditionalResource(ResourceType.GROUPS) :
                                        getAdditionalResource(resourceToDuplicate.type)
                    }
                    onClickNew={handleCreateClick}
                />
            )}

            {/* Publish Modal */}
            {resourceToPublish && (
                <PublishComponent
                    open={publishModalOpen}
                    componentName={resourceToPublish.name}
                    publishing={publishing}
                    onClose={(): void => {
                        if (!publishing) {
                            setPublishModalOpen(false);
                            setResourceToPublish(null);
                        }
                    }}
                    onConfirm={handlePublishConfirm}
                />
            )}

            {/* Move Modal */}
            {resourceToMove && (
                <MoveResource
                    open={moveModalOpen}
                    title={`Move ${resourceToMove.type === ResourceType.GROUPS ? 'Working Area' :
                        resourceToMove.type === ResourceType.SAFETY_MODELS ? 'Safety Model' :
                            resourceToMove.type === ResourceType.EVENT_SEQUENCES ? 'Safety View' :
                                'Resource'} - ${resourceToMove.name}`}
                    onClose={(): void => {
                        setMoveModalOpen(false);
                        setResourceToMove(null);
                    }}
                    onConfirm={(targetId: number | null): void => {
                        setMoveModalOpen(false);
                        if (resourceToMove.object) {
                            let movePromise: Promise<any> | undefined;

                            switch (resourceToMove.type) {
                                case ResourceType.GROUPS:
                                    movePromise = GroupService.moveGroup(resourceToMove.object.id, targetId).then(() => {
                                        setWorkspaceObjects({ objectType: ResourceType.GROUPS });
                                    });
                                    break;

                                case ResourceType.SAFETY_MODELS:
                                    if (targetId === null) break;
                                    movePromise = safetyService.moveSafetyModel(resourceToMove.object.id, targetId).then(() => {
                                        setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
                                    });
                                    break;

                                case ResourceType.EVENT_SEQUENCES:
                                    if (targetId === null) break;
                                    movePromise = eventSequenceService.moveEventSequence(resourceToMove.object.id, targetId).then(() => {
                                        setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
                                    });
                                    break;

                                default:
                                    toast.error(`Move not implemented for ${resourceToMove.type}`);
                                    setResourceToMove(null);
                                    return;
                            }
                            if (movePromise) {
                                movePromise.then(() => {
                                    toast.success(`"${resourceToMove.name}" moved successfully`);
                                }).catch((error) => {
                                    toast.error(`Error moving ${resourceToMove.name} - ${error.message}`);
                                }).finally(() => {
                                    setResourceToMove(null);
                                });
                            }
                        }
                    }}
                    object={{ objectType: resourceToMove.type, object: resourceToMove.object }}
                    data={
                        resourceToMove.type === ResourceType.GROUPS ?
                            excludeGroupAndDescendants(getAdditionalResource(ResourceType.GROUPS), resourceToMove.object, workspaceObjects) :
                            resourceToMove.type === ResourceType.SAFETY_MODELS ?
                                filterOnGroupCanEditResources(getAdditionalResource(ResourceType.GROUPS)) :
                                filterOnGroupCanEditResources(getAdditionalResource(ResourceType.SAFETY_MODELS))
                    }
                />
            )}

            {/* Leave Group Modal */}
            {groupToLeave && groupToLeave.type === ResourceType.GROUPS && (
                <LeaveGroupModal
                    leaveGroup={leaveGroupModalOpen}
                    group={groupToLeave.object}
                    onConfirm={(): void => {
                        setLeaveGroupModalOpen(false);
                        if (groupToLeave.object) {
                            const childrenIds = getTreeItemDescendantIds(groupToLeave.object,
                                ResourceType.GROUPS,
                                workspaceObjects?.resources ?? []);
                            GroupService.leaveGroupById(groupToLeave.object).then(() => {
                                setDeletedWorkspaceObject({
                                    objectType: ResourceType.GROUPS,
                                    id: groupToLeave.object.id!,
                                    children: childrenIds
                                });
                                setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS });
                                toast.success(`Successfully left working area "${groupToLeave.name}"`);
                            }).catch((error) => {
                                toast.error(error.message);
                            }).finally(() => {
                                setGroupToLeave(null);
                            });
                        }
                    }}
                    onClose={(): void => {
                        setLeaveGroupModalOpen(false);
                        setGroupToLeave(null);
                    }}
                />
            )}
            
            {/* Export Event Sequence Modal */}
            {exportSafetyModel && (
                <ExportEventSequenceSelectorDialogBox
                    open={showExportEventSequence}
                    safetyModel={exportSafetyModel.object}
                    eventSequenceList={exportEventSequenceList}
                    onClose={(): void => {
                        setShowExportEventSequence(false);
                        setExportSafetyModel(null);
                        setExportEventSequenceList([]);
                    }}
                    onExport={(eventSequenceSelected: EventSequenceSummary | undefined): void => {
                        setShowExportEventSequence(false);
                        if (eventSequenceSelected) {
                            exportEventSequence(eventSequenceSelected);
                        }
                        setExportSafetyModel(null);
                        setExportEventSequenceList([]);
                    }}
                />
            )}
        </Box>
    );
}